import { useEffect, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-hot-toast";
import {
  useAddQuizMutation,
  useGetQuizQuery,
  useGetQuizzesQuery,
  useUpdateQuizMutation,
} from "@features/quizzes/quizzesApi";
import { useGetVideosQuery } from "@features/videos/videosApi";
import InputWrapper from "@common/components/InputWrapper";

const QuizModal = ({ quizId = "", open, closeModal }) => {
  const [question, setQuestion] = useState("");
  const [video_id, setVideoId] = useState("");
  const [video_title, setVideoTitle] = useState("");
  const [options, setOptions] = useState([
    { option: "", isCorrect: false },
    { option: "", isCorrect: false },
    { option: "", isCorrect: false },
    { option: "", isCorrect: false },
  ]);

  const { data: quizzes } = useGetQuizzesQuery();

  const [addQuiz, { isLoading }] = useAddQuizMutation();

  const [updateQuiz, { isLoading: isUpdateLoading }] = useUpdateQuizMutation();

  const { data: quiz } = useGetQuizQuery(quizId);

  const { data: videos } = useGetVideosQuery();

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
    },
  };

  Modal.setAppElement("#root");

  useEffect(() => {
    const { question, video_id, video_title, options } = quiz || {};
    if (quizId) {
      setQuestion(question);
      setVideoId(+video_id);
      setVideoTitle(video_title);
      setOptions(options);
    }
  }, [quiz, quizId]);

  const handleSelectChange = (e) => {
    setVideoId(+e.target.options[e.target.selectedIndex].dataset.id);
    setVideoTitle(e.target.options[e.target.selectedIndex].text);
  };

  const handleOptionChange = (index, optionValue) => {
    setOptions((prevOptions) =>
      prevOptions.map((option, i) =>
        i === index ? { ...option, option: optionValue } : option
      )
    );
  };

  const handleOptionCorrectness = (index, isCorrectValue) => {
    setOptions((prevOptions) =>
      prevOptions.map((option, i) =>
        i === index ? { ...option, isCorrect: isCorrectValue } : option
      )
    );
  };

  const resetForm = () => {
    setQuestion("");
    setVideoId("");
    setVideoTitle("");
    setOptions("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      question,
      video_id,
      video_title,
      options,
    };
    let videoItem = quizzes?.find((q) => q.video_title === video_title);

    if (videoItem === undefined) {
      const response = await addQuiz(data);
      if (response?.data) {
        resetForm();
        closeModal();
        toast.success("Quiz added successfully!");
      } else {
        toast.error("Quiz added failed!");
      }
    } else {
      toast.error("Video already added to another quiz!");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const data = {
      question,
      video_id,
      video_title,
      options,
    };

    // Check if the selected video is the same as the current quiz's video
    if (video_title !== quizzes.find((q) => q.id === quizId).video_title) {
      let videoItem = quizzes?.find((q) => q.video_title === video_title);
      if (videoItem !== undefined) {
        toast.error("Video already added to another quiz!");
        return;
      }
    }

    const response = await updateQuiz({ id: quizId, data });
    if (response?.data) {
      closeModal();
      toast.success("Quiz updated successfully!");
    } else {
      toast.error("Quiz updated failed!");
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Example Modal"
    >
      <h2 className="text-xl text-center font-bold">
        {quizId ? "Update" : "Add"} Quiz
      </h2>
      <button className="btn-modal-close" onClick={closeModal}>
        &times;
      </button>
      <form
        className="space-y-6"
        onSubmit={quizId ? handleUpdate : handleSubmit}
      >
        <div className="rounded-md shadow-sm -space-y-px">
          <InputWrapper
            id="title"
            name="title"
            type="text"
            required
            className="form-input rounded"
            placeholder="Assignment Title"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <div style={{ marginBottom: "-7px" }}>
            <label htmlFor="description" className="form-label sr-only">
              Video Title
            </label>
            <select
              name="video_title"
              id="video_title"
              required
              className="form-input rounded"
              value={video_title}
              onChange={handleSelectChange}
            >
              <option value="" disabled>
                Select Video
              </option>
              {videos?.length > 0 &&
                videos.map((video) => (
                  <option key={video.id} value={video.title} data-id={video.id}>
                    {video.title}
                  </option>
                ))}
            </select>
          </div>

          <div className="option-field flex gap-4 flex-wrap">
            <div className="flex gap-2 items-baseline">
              <input
                type="checkbox"
                checked={options && options[0].isCorrect}
                onChange={(e) => handleOptionCorrectness(0, e.target.checked)}
              />
              <InputWrapper
                id="option1"
                name="option1"
                type="text"
                required
                className="form-input rounded"
                placeholder="Option 1"
                value={options && options[0]?.option}
                onChange={(e) => handleOptionChange(0, e.target.value)}
              />
            </div>

            <div className="flex gap-2 items-baseline">
              <input
                type="checkbox"
                checked={options && options[1].isCorrect}
                onChange={(e) => handleOptionCorrectness(1, e.target.checked)}
              />
              <InputWrapper
                id="option2"
                name="option2"
                type="text"
                required
                className="form-input rounded"
                placeholder="Option 2"
                value={options && options[1]?.option}
                onChange={(e) => handleOptionChange(1, e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <input
                type="checkbox"
                checked={options && options[2].isCorrect}
                onChange={(e) => handleOptionCorrectness(2, e.target.checked)}
              />
              <InputWrapper
                id="option3"
                name="option3"
                type="text"
                required
                className="form-input rounded"
                placeholder="Option 3"
                value={options && options[2]?.option}
                onChange={(e) => handleOptionChange(2, e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <input
                type="checkbox"
                checked={options && options[3].isCorrect}
                onChange={(e) => handleOptionCorrectness(3, e.target.checked)}
              />
              <InputWrapper
                id="option4"
                name="option4"
                type="text"
                required
                className="form-input rounded"
                placeholder="Option 4"
                value={options && options[3]?.option}
                onChange={(e) => handleOptionChange(3, e.target.value)}
              />
            </div>
          </div>

          <div className="submit-button-wrapper text-center">
            <button
              type="submit"
              disabled={quizId ? isUpdateLoading : isLoading}
              className="btn-submit font-bold w-full"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default QuizModal;
